/**
 * Who bought a gift voucher online: a private person or a company.
 *
 * A restaurant gift voucher is a multi-purpose voucher, so nobody can be sent a VAT invoice
 * for it. Every paid order gets a Voucher Purchase Confirmation instead (see
 * functions/src/domain/giftVouchers/documents). A company buyer leaves its details at
 * checkout so the confirmation carries them; the shop validates them with
 * `normalizeCompanyDetails` before paying, and the API validates them again before storing.
 * A buyer who checked out as Private can add them later through the link in their email.
 */

export type GiftVoucherBuyerType = 'private' | 'company';

export interface GiftVoucherCompanyDetails {
  /** Statutory name, as on the company register. */
  legal_name: string;
  vat_number?: string;
  /** KBO / ondernemingsnummer (BE), KvK-nummer (NL)… */
  registration_number?: string;
  street: string;
  postal_code: string;
  city: string;
  /** ISO-2. */
  country: string;
  /** Where the purchase confirmation is sent. */
  billing_email: string;
  /** The buyer's purchase order or cost-centre reference, printed on the document. */
  po_reference?: string;
}

export type CompanyDetailsField = keyof GiftVoucherCompanyDetails;
export type CompanyDetailsError = 'required' | 'invalid';
export type CompanyDetailsErrors = Partial<Record<CompanyDetailsField, CompanyDetailsError>>;

export type NormalizeCompanyDetailsResult =
  | { ok: true; company: GiftVoucherCompanyDetails }
  | { ok: false; errors: CompanyDetailsErrors };

/** Required fields, in the order the form shows them. */
export const COMPANY_REQUIRED_FIELDS: CompanyDetailsField[] = [
  'legal_name',
  'street',
  'postal_code',
  'city',
  'country',
  'billing_email',
];

export const COMPANY_OPTIONAL_FIELDS: CompanyDetailsField[] = ['vat_number', 'registration_number', 'po_reference'];

const MAX_FIELD_LENGTH = 200;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * VAT number shapes per EU country (after the two-letter prefix). Only the format is checked
 * here; whether the number is registered is VIES's job, and that check never blocks a sale.
 */
const EU_VAT_PATTERNS: Record<string, RegExp> = {
  AT: /^U\d{8}$/,
  BE: /^[01]\d{9}$/,
  BG: /^\d{9,10}$/,
  CY: /^\d{8}[A-Z]$/,
  CZ: /^\d{8,10}$/,
  DE: /^\d{9}$/,
  DK: /^\d{8}$/,
  EE: /^\d{9}$/,
  EL: /^\d{9}$/,
  ES: /^[0-9A-Z]\d{7}[0-9A-Z]$/,
  FI: /^\d{8}$/,
  FR: /^[0-9A-Z]{2}\d{9}$/,
  HR: /^\d{11}$/,
  HU: /^\d{8}$/,
  IE: /^\d[0-9A-Z+*]\d{5}[A-Z]{1,2}$/,
  IT: /^\d{11}$/,
  LT: /^(\d{9}|\d{12})$/,
  LU: /^\d{8}$/,
  LV: /^\d{11}$/,
  MT: /^\d{8}$/,
  NL: /^\d{9}B\d{2}$/,
  PL: /^\d{10}$/,
  PT: /^\d{9}$/,
  RO: /^\d{2,10}$/,
  SE: /^\d{12}$/,
  SI: /^\d{8}$/,
  SK: /^\d{10}$/,
  XI: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
};

/** Greece files VAT under EL, not its ISO code GR. */
const VAT_PREFIX_FOR_COUNTRY: Record<string, string> = { GR: 'EL' };

export function vatPrefixForCountry(countryIso2: string | undefined): string | undefined {
  if (!countryIso2) return undefined;
  const iso = countryIso2.toUpperCase();
  return VAT_PREFIX_FOR_COUNTRY[iso] || iso;
}

export function isEuVatPrefix(prefix: string | undefined): boolean {
  return !!prefix && prefix in EU_VAT_PATTERNS;
}

/** Strips spaces, dots and dashes; uppercases. */
export function compactVatNumber(value: string): string {
  return value.toUpperCase().replace(/[\s.\-/]/g, '');
}

/**
 * Normalises a VAT number for the given country and checks its shape. A Belgian buyer who
 * types "0123.456.789" gets "BE0123456789" — the country prefix is added when missing.
 * Numbers from outside the EU only get a loose length check.
 */
export function normalizeVatNumber(
  value: string,
  countryIso2?: string
): { ok: true; vat: string } | { ok: false; vat: string } {
  let vat = compactVatNumber(value);
  const countryPrefix = vatPrefixForCountry(countryIso2);
  const hasPrefix = /^[A-Z]{2}/.test(vat) && !/^\d/.test(vat);
  if (!hasPrefix && countryPrefix && isEuVatPrefix(countryPrefix)) vat = `${countryPrefix}${vat}`;

  const prefix = vat.slice(0, 2);
  const pattern = EU_VAT_PATTERNS[prefix];
  if (pattern) return pattern.test(vat.slice(2)) ? { ok: true, vat } : { ok: false, vat };
  return /^[0-9A-Z]{4,20}$/.test(vat) ? { ok: true, vat } : { ok: false, vat };
}

const clean = (value: unknown): string =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').slice(0, MAX_FIELD_LENGTH) : '';

export function normalizeCompanyDetails(raw: unknown): NormalizeCompanyDetailsResult {
  const input = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const errors: CompanyDetailsErrors = {};
  const company: Record<string, string> = {};

  for (const field of COMPANY_REQUIRED_FIELDS) {
    const value = clean(input[field]);
    if (!value) errors[field] = 'required';
    else company[field] = value;
  }
  for (const field of COMPANY_OPTIONAL_FIELDS) {
    const value = clean(input[field]);
    if (value) company[field] = value;
  }

  if (company.country) {
    company.country = company.country.toUpperCase();
    if (!/^[A-Z]{2}$/.test(company.country)) errors.country = 'invalid';
  }
  if (company.billing_email) {
    company.billing_email = company.billing_email.toLowerCase();
    if (!EMAIL_RE.test(company.billing_email)) errors.billing_email = 'invalid';
  }
  if (company.vat_number) {
    const vat = normalizeVatNumber(company.vat_number, errors.country ? undefined : company.country);
    company.vat_number = vat.vat;
    if (!vat.ok) errors.vat_number = 'invalid';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, company: company as unknown as GiftVoucherCompanyDetails };
}

export function parseBuyerType(value: unknown): GiftVoucherBuyerType {
  return value === 'company' ? 'company' : 'private';
}

export function formatCompanyAddress(company: Pick<GiftVoucherCompanyDetails, 'street' | 'postal_code' | 'city' | 'country'>): string[] {
  return [company.street, [company.postal_code, company.city].filter(Boolean).join(' '), company.country].filter(
    (line): line is string => Boolean(line && line.trim())
  );
}

/** Public explainer article on why the document carries no VAT. */
export const VOUCHER_VAT_EXPLAINER_URLS: Record<'en' | 'nl', string> = {
  en: 'https://gift.10seat.com/help/en/gift-vouchers-and-vat',
  nl: 'https://gift.10seat.com/help/nl/cadeaubonnen-en-btw',
};

export function voucherVatExplainerUrl(locale: string | undefined, merchantCountry?: string): string {
  const lang = (locale || 'en').split('-')[0].toLowerCase();
  const base = lang === 'nl' ? VOUCHER_VAT_EXPLAINER_URLS.nl : VOUCHER_VAT_EXPLAINER_URLS.en;
  const country = (merchantCountry || '').toUpperCase();
  return country === 'BE' || country === 'NL' ? `${base}?country=${country.toLowerCase()}` : base;
}
